// src/components/StatusBar.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  GitBranch, 
  Circle, 
  AlertCircle, 
  Loader,
  X,
  Check,
  Save
} from 'lucide-react';
import './styles.css';

interface StatusBarProps {
  isGitRepo: boolean;
  gitStatus: any;
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

const StatusBar: React.FC<StatusBarProps> = ({
  isGitRepo,
  gitStatus,
  hasUnsavedChanges,
  isLoading,
  error,
  onClearError
}) => {
  // Calculate git status summary
  const getGitSummary = () => {
    if (!gitStatus) return null;
    
    const modified = gitStatus.modified?.length || 0;
    const created = gitStatus.created?.length || 0;
    const deleted = gitStatus.deleted?.length || 0;
    const notAdded = gitStatus.not_added?.length || 0;
    
    const total = modified + created + deleted + notAdded;
    
    if (total === 0) return { text: 'Clean', type: 'clean' };
    
    const parts = [];
    if (modified > 0) parts.push(`${modified} modified`);
    if (created > 0) parts.push(`${created} added`);
    if (deleted > 0) parts.push(`${deleted} deleted`);
    if (notAdded > 0) parts.push(`${notAdded} untracked`);
    
    return { 
      text: parts.join(', '), 
      type: 'changes' 
    };
  };

  const gitSummary = getGitSummary();

  return (
    <div className="status-bar">
      <div className="status-left">
        {/* Git Status */}
        {isGitRepo && (
          <div className="status-item git-status">
            <GitBranch size={14} />
            <span className="status-label">Git:</span>
            {gitSummary ? (
              <span className={`status-value ${gitSummary.type}`}>
                {gitSummary.type === 'clean' ? (
                  <Check size={12} className="status-icon" />
                ) : (
                  <Circle size={8} className="status-icon" />
                )}
                {gitSummary.text}
              </span>
            ) : (
              <span className="status-value">Not initialized</span>
            )}
          </div>
        )}
        
        {/* Save Status */}
        {hasUnsavedChanges && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="status-item unsaved"
          >
            <Save size={14} />
            <span>Unsaved changes</span>
          </motion.div>
        )}
      </div>

      <div className="status-center">
        {/* Loading Indicator */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="status-loading"
            >
              <Loader size={14} className="spinner" />
              <span>Loading...</span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="status-error"
            >
              <AlertCircle size={14} />
              <span className="error-message">{error}</span>
              <button 
                onClick={onClearError}
                className="error-close"
              >
                <X size={14} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="status-right">
        {/* Application Info */}
        <div className="status-item app-info">
          <span className="app-name">Time Files</span>
          <span className="app-version">v1.0.0</span>
        </div>
      </div>
    </div>
  );
};

export default StatusBar;